// Updated: accept .webp/.png/.jpg uploads up to 20MB, auto-convert to WebP at 80% quality; active upload feedback via spinner label and optimistic preview per image
import React, { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { Loader2 } from 'lucide-react';
import Collapsible from '../components/Collapsible';
import RichTextEditor from '../../RichTextEditor';
import { useToastContext } from '../../../contexts/ToastContext';
import { validateImageFile, convertToWebp } from '../../../utils/imageUpload';

interface AboutImages {
  main_image: string;
  secondary_image: string;
}

const AboutSection = () => {
  const [images, setImages] = useState<AboutImages>({
    main_image: '',
    secondary_image: ''
  });
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [editorKey, setEditorKey] = useState(0);
  const [uploadingKey, setUploadingKey] = useState<'about_main_image' | 'about_secondary_image' | null>(null);
  const [previews, setPreviews] = useState<{ main_image: string; secondary_image: string }>({ main_image: '', secondary_image: '' });
  const { addToast } = useToastContext();

  const uploadImage = async (event: React.ChangeEvent<HTMLInputElement>, key: 'about_main_image' | 'about_secondary_image') => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }

    const rawFile = event.target.files[0];
    const validation = validateImageFile(rawFile);
    if (!validation.ok) {
      addToast(validation.message, 'error');
      event.target.value = '';
      return;
    }

    const previewKey = key === 'about_main_image' ? 'main_image' : 'secondary_image';
    const objectUrl = URL.createObjectURL(rawFile);
    setPreviews(prev => ({ ...prev, [previewKey]: objectUrl }));
    setUploadingKey(key);

    try {
      const file = await convertToWebp(rawFile);

      // Delete old image if exists
      const oldImage = images[key === 'about_main_image' ? 'main_image' : 'secondary_image'];
      if (oldImage) {
        const oldFilePath = oldImage.split('/').pop();
        if (oldFilePath) {
          await supabase.storage
            .from('images')
            .remove([`about/${oldFilePath}`]);
        }
      }

      const fileName = `${key}-${Date.now()}.webp`;
      const filePath = `about/${fileName}`;

      // Upload new image
      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      // Update settings
      const { error: updateError } = await supabase
        .from('settings')
        .upsert({ 
          key,
          value: publicUrl
        }, {
          onConflict: 'key'
        });

      if (updateError) throw updateError;

      setImages(prev => ({
        ...prev,
        [key === 'about_main_image' ? 'main_image' : 'secondary_image']: publicUrl
      }));
      addToast('Afbeelding succesvol bijgewerkt', 'success');
    } catch (error) {
      console.error('Error uploading image:', error);
      addToast('Er is een fout opgetreden bij het uploaden van de afbeelding', 'error');
    } finally {
      URL.revokeObjectURL(objectUrl);
      setPreviews(prev => ({ ...prev, [previewKey]: '' }));
      setUploadingKey(null);
      event.target.value = '';
    }
  };

  const handleTextChange = (value: string) => {
    // Replace multiple newlines with \n\n
    const formattedText = value.replace(/\n{2,}/g, '\\n\\n');
    setText(formattedText);
  };

  const handleTitleChange = (value: string) => {
    // Replace single newline with \n
    const formattedText = value.replace(/\n/g, '\\n');
    setTitle(formattedText);
  };

  useEffect(() => {
    fetchImages();
    fetchText();
  }, []);

  const fetchText = async () => {
    try {
      const { data: titleData } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'about_title')
        .single();

      const { data: textData } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'about_text')
        .single();

      const newTitle = titleData?.value || '';
      const newText = textData?.value || '';
      
      setTitle(newTitle);
      setText(newText);
      setEditorKey(prev => prev + 1); // Force editor re-render
    } catch (error) {
      console.error('Error fetching about text:', error);
    }
  };

  const fetchImages = async () => {
    try {
      const { data: mainImage, error: mainError } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'about_main_image')
        .single();

      const { data: secondaryImage, error: secondaryError } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'about_secondary_image')
        .single();

      if (mainError && mainError.code !== 'PGRST116') throw mainError;
      if (secondaryError && secondaryError.code !== 'PGRST116') throw secondaryError;

      setImages({
        main_image: mainImage?.value || '',
        secondary_image: secondaryImage?.value || ''
      });
    } catch (error) {
      console.error('Error fetching about images:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateImage = async (key: 'about_main_image' | 'about_secondary_image', value: string) => {
    try {
      const { error } = await supabase
        .from('settings')
        .upsert({ 
          key,
          value
        }, {
          onConflict: 'key'
        });

      if (error) throw error;
      
      // Update local state
      setImages(prev => ({
        ...prev,
        [key === 'about_main_image' ? 'main_image' : 'secondary_image']: value
      }));
      setFeedback('Afbeelding succesvol bijgewerkt');
      setFeedbackType('success');
      setTimeout(() => setFeedback(''), 3000);
    } catch (error) {
      console.error('Error updating image:', error);
      setFeedback('Er is een fout opgetreden bij het bijwerken van de afbeelding');
      setFeedbackType('error');
      setTimeout(() => setFeedback(''), 3000);
    }
  };

  const updateText = async (key: 'about_title' | 'about_text', value: string) => {
    try {
      const { error } = await supabase
        .from('settings')
        .upsert({ 
          key,
          value
        }, {
          onConflict: 'key'
        });

      if (error) throw error;
      
      addToast('Tekst succesvol bijgewerkt', 'success');
    } catch (error) {
      console.error('Error updating text:', error);
      addToast('Er is een fout opgetreden bij het bijwerken van de tekst', 'error');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      
      <div className="space-y-6">
        <Collapsible title="Titel aanpassen">
          <div className="space-y-8">
            <div>
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                  <textarea
                    value={title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
                    rows={2}
                    placeholder="Voer de titel in (gebruik \n voor nieuwe regel)"
                  />
                </div>
                <button
                  onClick={() => updateText('about_title', title)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#E37C64] hover:bg-[#5A130E]"
                >
                  Tekst bijwerken
                </button>
              </div>
            </div>
          </div>
        </Collapsible>

        <Collapsible title="Beschrijving aanpassen">
          <div className="space-y-8">
            <div>
              <div className="flex gap-4 items-end">
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Beschrijving tekst
                  </label>
                  <div className="space-y-4">
                    <RichTextEditor
                      key={editorKey}
                      content={text}
                      onChange={handleTextChange}
                    />
                    <button
                      onClick={() => updateText('about_text', text)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#E37C64] hover:bg-[#5A130E]"
                    >
                      Tekst aanpassen
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Collapsible>

        <Collapsible title="Hoofdafbeelding aanpassen">
          <div className="space-y-8">
            <div>
              <div className="flex gap-4 items-end">
                <label className={`flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white ${uploadingKey === 'about_main_image' ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}>
                  <input
                    type="file"
                    accept="image/webp,image/png,image/jpeg"
                    onChange={(e) => uploadImage(e, 'about_main_image')}
                    disabled={uploadingKey !== null}
                    className="hidden"
                  />
                  {uploadingKey === 'about_main_image' ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Bezig met uploaden...</>
                  ) : (
                    'Kies bestand'
                  )}
                </label>
              </div>
              {(previews.main_image || images.main_image) && (
                <div className="mt-4 relative w-64 h-64">
                  <img
                    src={previews.main_image || images.main_image}
                    alt="Main about section"
                    className={`w-64 h-64 object-cover rounded-lg transition-opacity ${uploadingKey === 'about_main_image' ? 'opacity-70' : ''}`}
                  />
                  {uploadingKey === 'about_main_image' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
                      <Loader2 className="w-8 h-8 text-white animate-spin" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Collapsible>

        <Collapsible title="Tweede afbeelding aanpassen">
          <div className="space-y-8">
            <div>
              <div className="flex gap-4 items-end">
                <label className={`flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white ${uploadingKey === 'about_secondary_image' ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}>
                  <input
                    type="file"
                    accept="image/webp,image/png,image/jpeg"
                    onChange={(e) => uploadImage(e, 'about_secondary_image')}
                    disabled={uploadingKey !== null}
                    className="hidden"
                  />
                  {uploadingKey === 'about_secondary_image' ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Bezig met uploaden...</>
                  ) : (
                    'Kies bestand'
                  )}
                </label>
              </div>
              {(previews.secondary_image || images.secondary_image) && (
                <div className="mt-4 relative w-64 h-96">
                  <img
                    src={previews.secondary_image || images.secondary_image}
                    alt="Secondary about section"
                    className={`w-64 h-96 object-cover rounded-lg transition-opacity ${uploadingKey === 'about_secondary_image' ? 'opacity-70' : ''}`}
                  />
                  {uploadingKey === 'about_secondary_image' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
                      <Loader2 className="w-8 h-8 text-white animate-spin" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Collapsible>
      </div>
    </div>
  );
};

export default AboutSection;